import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDevXGPTClient } from '../api/client';
import type { APIConfig, APIError, Assistant } from '../api/types';

export interface UseAssistantsOptions extends Omit<APIConfig, 'assistantId'> {
  autoFetch?: boolean;
  defaultAssistantId?: string;
  onAssistantChange?: (assistant: Assistant | null) => void;
}

export interface UseAssistantsReturn {
  // State
  assistants: Assistant[];
  selectedAssistant: Assistant | null;
  defaultAssistant: Assistant | null;
  isLoading: boolean;
  error: APIError | null;

  // Actions
  fetchAssistants: () => Promise<void>;
  selectAssistant: (assistant: Assistant | null) => void;
  selectAssistantById: (assistantId: string) => void;
  resetToDefault: () => void;
  clearError: () => void;

  // Utilities
  getAssistantById: (assistantId: string) => Assistant | undefined;
  getPromptsByAssistant: (assistantId?: string) => string[];
}

const ASK_DEVX_TITLE = 'Ask DevX';

/**
 * Find fallback assistant from list using priority strategy.
 *
 * This is used as the fallback when a preferred assistant ID is not found.
 *
 * @param assistantList - List of available assistants
 * @param invalidId - Optional ID that was invalid (for logging warning)
 */
function getFallbackAssistant(assistantList: Assistant[], invalidId?: string): Assistant | null {
  if (invalidId) {
    const availableIds = assistantList.map((a) => a.assistant_id).join(', ');
    console.warn(
      `[DevXGPT] Provided assistantId "${invalidId}" not found in available assistants. ` +
        `Available IDs: [${availableIds}]. Falling back to [${ASK_DEVX_TITLE}] or first available assistant.`
    );
  }

  const askDevXAssistant = assistantList.find((a) => a.title === ASK_DEVX_TITLE);
  return askDevXAssistant || assistantList[0] || null;
}

/**
 * Find a valid default assistant with robust fallback mechanism.
 *
 * Selection strategy (in priority order):
 * 1. If preferredId is provided and exists in the list, use it
 * 2. If preferredId is provided but NOT found, log a warning and fall back to Ask DevX
 * 3. If no preferredId, use Ask DevX if available
 * 4. If Ask DevX not available, use first assistant in list
 * 5. Return null if assistant list is empty
 *
 * This ensures the component never fails due to invalid assistant IDs passed by teams.
 */
function findDefaultAssistant(
  assistantList: Assistant[],
  preferredId?: string
): Assistant | null {
  if (assistantList.length === 0) return null;

  if (preferredId) {
    const found = assistantList.find((a) => a.assistant_id === preferredId);
    if (found) return found;
  }

  return getFallbackAssistant(assistantList, preferredId);
}

/**
 * React Hook for Managing DevXGPT Assistants
 *
 * Handles fetching available assistants, managing selection, and providing
 * prompt suggestions based on the selected assistant.
 *
 */
export function useAssistants(options: UseAssistantsOptions): UseAssistantsReturn {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<APIError | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const client = useMemo(() => {
    return createDevXGPTClient({
      authToken: options.authToken,
      useMock: options.useMock,
    });
  }, [options.authToken, options.useMock]);

  const effectiveDefaultId = useMemo(() => {
    return options.defaultAssistantId;
  }, [options.defaultAssistantId]);

  const defaultAssistant = useMemo(() => {
    return findDefaultAssistant(assistants, effectiveDefaultId);
  }, [assistants, effectiveDefaultId]);

  const fetchAssistants = useCallback(async () => {
    if (!client) return;

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getAssistants();
      // Accept both array and object return shapes for robustness
      const assistantsArray = Array.isArray(response) ? response : response.assistants;
      console.log('Fetched assistants from API:', assistantsArray);

      // if (!isMountedRef.current) return;

      setAssistants(assistantsArray);
      setIsLoading(false); // Ensure loading state is cleared after successful fetch
      console.log('Setting assistants state:', assistantsArray);
    } catch (err) {
      if (!isMountedRef.current) return;

      if (err instanceof Error && err.name === 'AbortError') return;

      const apiError: APIError = {
        message: err instanceof Error ? err.message : 'Failed to fetch assistants',
      };
      setError(apiError);
      console.error('Error fetching assistants:', apiError);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [client, setAssistants, setIsLoading, setError]);

  const selectAssistant = useCallback(
    (assistant: Assistant | null) => {
      setSelectedAssistant(assistant);
      setError(null);
      options.onAssistantChange?.(assistant);
    },
    [options]
  );

  /**
   * Select assistant by ID with fallback logic.
   */
  const selectAssistantById = useCallback(
    (assistantId: string) => {
      const assistant = assistants.find((a) => a.assistant_id === assistantId);
      if (assistant) {
        selectAssistant(assistant);
        return;
      }

      // Fallback if assistant ID not found
      const fallbackAssistant = getFallbackAssistant(assistants, assistantId);
      if (fallbackAssistant) {
        selectAssistant(fallbackAssistant);
      }
    },
    [assistants, selectAssistant]
  );

  const resetToDefault = useCallback(() => {
    if (defaultAssistant) {
      selectAssistant(defaultAssistant);
    }
  }, [defaultAssistant, selectAssistant]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getAssistantById = useCallback(
    (assistantId: string) => {
      return assistants.find((a) => a.assistant_id === assistantId);
    },
    [assistants]
  );

  const getPromptsByAssistant = useCallback(
    (assistantId?: string) => {
      if (!assistantId) return [];
      const assistant = assistants.find((a) => a.assistant_id === assistantId);
      return assistant?.prompts || [];
    },
    [assistants]
  );

  useEffect(() => {
    if (options.autoFetch) {
      console.log('Auto-fetching assistants...');
      fetchAssistants();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchAssistants, options.autoFetch]);

  useEffect(() => {
    if (!selectedAssistant && defaultAssistant) {
      console.log('Selecting default assistant:', defaultAssistant);
      selectAssistant(defaultAssistant);
    }
  }, [selectedAssistant, defaultAssistant, selectAssistant]);

  // Log assistants whenever they change (for debugging async state)
  useEffect(() => {
    console.log('assistants in DevXGPTChat (from useAssistants):', assistants);
  }, [assistants]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // State
    assistants,
    selectedAssistant,
    defaultAssistant,
    isLoading,
    error,

    // Actions
    fetchAssistants,
    selectAssistant,
    selectAssistantById,
    resetToDefault,
    clearError,

    // Utilities
    getAssistantById,
    getPromptsByAssistant,
  };
}
